import React, { useState } from "react";
import {
  Sparkles, Loader2, Image as ImageIcon,
  CheckCircle2, Gift, Package2, Gem, Crown, Wand2, Layers, Eye, X, Copy, Printer, Download,
} from "lucide-react";
import { Button } from "./ui/button";
import { TIERS, buildTierProduct } from "../lib/productBuilder";
import { downloadText, downloadHtml, downloadDataUrl, slugify } from "../lib/download";
import { buildProductHtmlDocument, mdToHtml } from "../lib/mdToHtml";
import { toast } from "sonner";

const TIER_META = [
  { key: "free_lead_magnet", icon: Gift,     accent: "from-emerald-500 to-teal-400",   border: "border-emerald-500/40", badgeBg: "bg-emerald-500/15", badgeText: "text-emerald-200" },
  { key: "low_ticket",       icon: Package2, accent: "from-sky-500 to-cyan-400",       border: "border-sky-500/40",     badgeBg: "bg-sky-500/15",     badgeText: "text-sky-200" },
  { key: "premium",          icon: Gem,      accent: "from-amber-500 to-orange-400",   border: "border-amber-500/40",   badgeBg: "bg-amber-500/15",   badgeText: "text-amber-200" },
  { key: "high_ticket",      icon: Crown,    accent: "from-rose-500 to-fuchsia-400",   border: "border-rose-500/40",    badgeBg: "bg-rose-500/15",    badgeText: "text-rose-200" },
];

const initialState = () =>
  TIER_META.reduce((acc, t) => {
    acc[t.key] = { loading: false, progress: 0, stageLabel: "", result: null, error: null };
    return acc;
  }, {});

export const OfferProductBuilder = ({ apiKey, offerData }) => {
  const [state, setState] = useState(initialState);
  const [buildingAll, setBuildingAll] = useState(false);
  const [previewTier, setPreviewTier] = useState(null); // { key, label, title, markdown, cover }

  if (!offerData) return null;

  const patch = (key, p) =>
    setState((s) => ({ ...s, [key]: { ...s[key], ...p } }));

  const buildOne = async (tierKey) => {
    if (!apiKey) {
      toast.error("Connect your OpenAI key first");
      return null;
    }
    patch(tierKey, { loading: true, progress: 2, stageLabel: "Queuing…", error: null });
    try {
      const result = await buildTierProduct({
        apiKey,
        tierKey,
        offerData,
        onProgress: ({ progress, label }) =>
          patch(tierKey, { progress, stageLabel: label }),
      });
      patch(tierKey, { loading: false, progress: 100, stageLabel: "Ready", result });
      toast.success(`${TIERS[tierKey].label} ready`);
      return result;
    } catch (e) {
      patch(tierKey, { loading: false, progress: 0, error: e.message || "Build failed" });
      toast.error(e.message || "Build failed");
      return null;
    }
  };

  const buildAll = async () => {
    if (!apiKey) {
      toast.error("Connect your OpenAI key first");
      return;
    }
    setBuildingAll(true);
    try {
      // Fire all 4 in parallel — they share the same brand bible, so results stay cohesive.
      await Promise.all(TIER_META.map((t) => buildOne(t.key)));
      toast.success("All 4 products built");
    } finally {
      setBuildingAll(false);
    }
  };

  const downloadStyledHtml = (tierKey) => {
    const r = state[tierKey]?.result;
    if (!r) return;
    const niche = slugify(offerData?.niche || "offer");
    const fname = `${niche}-${tierKey}.html`;
    try {
      const html = buildProductHtmlDocument({
        title: r.title,
        markdown: r.markdown,
        coverDataUrl: r.cover,
        subtitle: TIERS[tierKey]?.label,
      });
      const ok = downloadHtml(fname, html);
      if (ok) {
        toast.success(`${fname} downloading`);
      } else {
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        toast.info("Opened in a new tab — use your browser's Save As…");
      }
    } catch {
      toast.error("Download blocked. Use Preview → Copy.");
    }
  };

  const downloadMarkdown = (tierKey) => {
    const r = state[tierKey]?.result;
    if (!r) return;
    const niche = slugify(offerData?.niche || "offer");
    const fname = `${niche}-${tierKey}.md`;
    const ok = downloadText(fname, r.markdown, "text/markdown");
    if (ok) {
      toast.success(`${fname} downloading`);
    } else {
      // Fallback — open blob in a new tab so the user can save it manually.
      try {
        const blob = new Blob([r.markdown], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        toast.info("Opened in a new tab — use your browser's Save As…");
      } catch {
        toast.error("Download blocked. Use Preview → Copy.");
      }
    }
  };

  const downloadCover = (tierKey) => {
    const r = state[tierKey]?.result;
    if (!r?.cover) return;
    const niche = slugify(offerData?.niche || "offer");
    const fname = `${niche}-${tierKey}-cover.png`;
    const ok = downloadDataUrl(fname, r.cover);
    if (ok) {
      toast.success(`${fname} downloading`);
    } else {
      window.open(r.cover, "_blank");
      toast.info("Opened in a new tab — right-click → Save Image As…");
    }
  };

  // The "visually appealing" path — renders markdown as a beautifully styled
  // HTML document in a new tab. User can Print → Save as PDF, copy with formatting
  // into Google Docs / Canva / Notion, etc.
  const openPrintView = (tierKey) => {
    const r = state[tierKey]?.result;
    if (!r) return;
    try {
      const html = buildProductHtmlDocument({
        title: r.title,
        markdown: r.markdown,
        coverDataUrl: r.cover,
        subtitle: TIERS[tierKey]?.label,
      });
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        toast.error("Popup blocked — allow popups and try again");
      } else {
        toast.success("Opened styled preview in a new tab");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Couldn't open preview");
    }
  };

  const openPreview = (tierKey) => {
    const r = state[tierKey]?.result;
    if (!r) return;
    setPreviewTier({ key: tierKey, ...r });
  };

  const copyMarkdown = async () => {
    if (!previewTier?.markdown) return;
    try {
      await navigator.clipboard.writeText(previewTier.markdown);
      toast.success("Markdown copied to clipboard");
    } catch {
      // Fallback: use a hidden textarea + execCommand
      try {
        const ta = document.createElement("textarea");
        ta.value = previewTier.markdown;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Markdown copied to clipboard");
      } catch {
        toast.error("Clipboard blocked — select the text manually");
      }
    }
  };

  const anyResult = TIER_META.some((t) => state[t.key]?.result);
  const masterLabel = buildingAll
    ? "Building all 4 products…"
    : anyResult
    ? "Re-Build all 4 products"
    : "Build all 4 products";

  return (
    <section className="rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-900 via-zinc-900/80 to-zinc-950 p-6 sm:p-8 relative overflow-hidden">
      <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full blur-3xl bg-gradient-to-br from-amber-500/20 via-rose-500/15 to-violet-500/20 pointer-events-none" />

      <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-5">
        <div className="space-y-2 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            <Layers className="h-3 w-3" strokeWidth={2} /> Product Builder
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight">
            Actually build the{" "}
            <span className="bg-gradient-to-r from-amber-400 via-rose-400 to-violet-400 bg-clip-text text-transparent">
              4 digital products
            </span>
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed">
            One click per rung = full product content (Markdown) + cover image + sales-page copy.
            All 4 share one brand — same niche, positioning, audience, and cover style.
          </p>
        </div>

        <button
          onClick={buildAll}
          disabled={buildingAll || !apiKey}
          data-testid="build-all-products-button"
          className="group relative h-12 px-6 rounded-full font-semibold text-[14px] text-white
                     bg-gradient-to-r from-amber-500 via-rose-500 to-violet-500
                     hover:from-amber-400 hover:via-rose-400 hover:to-violet-400
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all duration-300 shadow-[0_10px_36px_-10px_rgba(244,63,94,.55)]
                     hover:shadow-[0_16px_48px_-8px_rgba(168,85,247,.55)] hover:-translate-y-[1px]
                     flex items-center justify-center gap-2 whitespace-nowrap"
        >
          {buildingAll ? (
            <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} /> {masterLabel}</>
          ) : (
            <><Wand2 className="h-4 w-4" strokeWidth={2.5} /> {masterLabel}</>
          )}
        </button>
      </div>

      <div className="relative mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {TIER_META.map((meta) => {
          const tier = TIERS[meta.key];
          const s = state[meta.key];
          const Icon = meta.icon;
          const r = s.result;
          const isDone = !!r;
          const isLoading = s.loading;

          return (
            <div
              key={meta.key}
              data-testid={`product-card-${meta.key}`}
              className={`relative rounded-2xl border ${meta.border} bg-zinc-950/60 p-5 pt-6 space-y-4 overflow-hidden`}
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${meta.accent}`} />

              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${meta.accent} text-white shadow-lg`}>
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-display text-base font-bold text-white leading-tight">
                      {tier.label}
                    </div>
                    <div className={`inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold uppercase tracking-[0.2em] rounded-full px-2 py-0.5 ${meta.badgeBg} ${meta.badgeText}`}>
                      {tier.badge}
                    </div>
                  </div>
                </div>
                {isDone && (
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" strokeWidth={2} />
                )}
              </div>

              <p className="text-[12.5px] text-zinc-400 leading-relaxed">
                {tier.description}
              </p>
              <div className="text-[10px] font-mono text-zinc-500">
                {tier.size} · ~{tier.wordTarget.toLocaleString()} words
              </div>

              {/* Per-tier offer-ladder copy (what the AI generated in CreateOffer) */}
              {offerData?.offer_ladder?.[meta.key] && (
                <div className="rounded-lg border border-white/5 bg-zinc-900/60 px-3 py-2 text-[12px] text-zinc-300 leading-relaxed">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 block mb-1">
                    Your ladder rung
                  </span>
                  {offerData.offer_ladder[meta.key]}
                </div>
              )}

              {/* Progress bar during build */}
              {isLoading && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-zinc-400 inline-flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 text-cyan-400 animate-spin" strokeWidth={2} />
                      {s.stageLabel || "Working…"}
                    </span>
                    <span className="font-mono text-zinc-500">{Math.max(s.progress || 0, 2)}%</span>
                  </div>
                  <div className="relative h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${meta.accent} transition-all duration-500`}
                      style={{ width: `${Math.max(s.progress || 0, 2)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Result preview */}
              {isDone && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    {r.cover ? (
                      <img
                        src={r.cover}
                        alt={`${tier.label} cover`}
                        data-testid={`product-cover-${meta.key}`}
                        className="w-[88px] h-[132px] rounded-md object-cover border border-white/10 shrink-0 bg-black"
                      />
                    ) : (
                      <div className="w-[88px] h-[132px] rounded-md border border-dashed border-white/10 bg-zinc-900 flex items-center justify-center text-zinc-600 shrink-0">
                        <ImageIcon className="h-5 w-5" strokeWidth={1.8} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-0.5">
                        Generated title
                      </div>
                      <div className="text-sm font-semibold text-white leading-snug line-clamp-3">
                        {r.title}
                      </div>
                      <div className="mt-1.5 text-[10px] text-zinc-500">
                        {r.markdown?.length ? `${Math.round(r.markdown.length / 1000)}k chars markdown` : ""}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={() => openPrintView(meta.key)}
                      data-testid={`printview-${meta.key}`}
                      className={`h-9 rounded-full bg-gradient-to-r ${meta.accent} text-[#0a0a0a] text-xs font-semibold hover:brightness-110`}
                    >
                      <Printer className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} />
                      Styled preview
                    </Button>
                    <Button
                      type="button"
                      onClick={() => downloadStyledHtml(meta.key)}
                      data-testid={`download-${meta.key}`}
                      className="h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold"
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} />
                      Download
                    </Button>
                    <Button
                      type="button"
                      onClick={() => downloadMarkdown(meta.key)}
                      data-testid={`download-md-${meta.key}`}
                      className="h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-semibold"
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                      .md
                    </Button>
                    {r.cover && (
                      <Button
                        type="button"
                        onClick={() => downloadCover(meta.key)}
                        data-testid={`download-cover-${meta.key}`}
                        className="h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-semibold"
                      >
                        <ImageIcon className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                        Cover
                      </Button>
                    )}
                    <Button
                      type="button"
                      onClick={() => openPreview(meta.key)}
                      data-testid={`preview-md-${meta.key}`}
                      className="h-9 rounded-full bg-white/5 hover:bg-white/10 text-zinc-300 text-xs font-semibold"
                    >
                      <Eye className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                      Preview
                    </Button>
                  </div>
                </div>
              )}

              {s.error && !isLoading && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2.5 text-[11px] text-red-200">
                  {s.error}
                </div>
              )}

              <Button
                type="button"
                onClick={() => buildOne(meta.key)}
                disabled={isLoading || buildingAll || !apiKey}
                data-testid={`build-${meta.key}-button`}
                className={`w-full h-10 rounded-full font-semibold text-xs bg-gradient-to-r ${meta.accent} text-[#0a0a0a] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" strokeWidth={2.5} /> Building…</>
                ) : isDone ? (
                  <><Sparkles className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} /> Re-Build this product</>
                ) : (
                  <><Sparkles className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} /> Build this product</>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Preview modal */}
      {previewTier && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-md"
          onClick={() => setPreviewTier(null)}
          data-testid="product-preview-modal"
        >
          <div
            className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl border border-white/10 bg-[#0a0a0a] shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10 shrink-0 bg-[#0a0a0a] rounded-t-2xl">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                  Product preview · {TIERS[previewTier.key]?.label}
                </div>
                <div className="font-display text-lg font-bold text-white leading-snug truncate mt-0.5">
                  {previewTier.title}
                </div>
              </div>
              <button
                onClick={() => setPreviewTier(null)}
                className="h-8 w-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white flex items-center justify-center shrink-0"
                data-testid="close-preview-modal"
                aria-label="Close preview"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto bg-[#0a0a0a]">
              {previewTier.cover && (
                <div className="p-5 pb-0">
                  <img
                    src={previewTier.cover}
                    alt="cover"
                    className="w-full max-w-[220px] rounded-xl border border-white/10 mx-auto shadow-xl"
                  />
                </div>
              )}
              <article
                className="px-6 py-5 product-preview-prose"
                dangerouslySetInnerHTML={{ __html: mdToHtml(previewTier.markdown || "") }}
              />
            </div>

            <div className="flex flex-wrap gap-2 p-4 border-t border-white/10 shrink-0 bg-[#0a0a0a] rounded-b-2xl">
              <Button
                type="button"
                onClick={() => openPrintView(previewTier.key)}
                data-testid="preview-printview-button"
                className="h-9 rounded-full bg-gradient-to-r from-amber-500 via-rose-500 to-violet-500 text-white text-xs font-semibold hover:brightness-110"
              >
                <Printer className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} />
                Open styled preview
              </Button>
              <Button
                type="button"
                onClick={() => downloadStyledHtml(previewTier.key)}
                data-testid="preview-download-html-button"
                className="h-9 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={2.5} />
                Download
              </Button>
              <Button
                type="button"
                onClick={() => downloadMarkdown(previewTier.key)}
                data-testid="preview-download-md-button"
                className="h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-semibold"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                Download .md
              </Button>
              {previewTier.cover && (
                <Button
                  type="button"
                  onClick={() => downloadCover(previewTier.key)}
                  data-testid="preview-download-cover-button"
                  className="h-9 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-semibold"
                >
                  <ImageIcon className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                  Download cover
                </Button>
              )}
              <Button
                type="button"
                onClick={copyMarkdown}
                data-testid="preview-copy-button"
                className="h-9 rounded-full bg-white/10 hover:bg-white/15 text-zinc-100 text-xs font-semibold"
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />
                Copy markdown
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default OfferProductBuilder;
